import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import { captureError } from '@/debug/debugStore';
import { getCaseById, updateCase } from '../api/services/casesApi';
import { useAuthContext } from '../contexts/useAuthContext';
import type { ParentRegistrationFormData, CreateCaseRequest } from '../types';
import { REGISTRATION_STEPS } from '../constants';
import {
  parseParentStep,
  parentRegistrationSchema,
} from '../schemas/parentRegistrationSchemas';
import { ZodError } from 'zod';
import TuitionDetailsStep from '../components/ParentCaseForm/TuitionDetailsStep';
import SubjectDurationStep from '../components/ParentCaseForm/SubjectDurationStep';
import LocationTimeStep from '../components/ParentCaseForm/LocationTimeStep';
import BudgetStep from '../components/ParentCaseForm/BudgetStep';
import { useI18n } from '@/i18n';
import { useNavigate } from '@/i18n/navigation';

const EditCasePage: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { backendUser } = useAuthContext();
  const queryClient = useQueryClient();
  const isResubmit = searchParams.get('resubmit') === 'true';
  const isZh = locale === 'zh-CN';

  // Page state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form data
  const [formData, setFormData] = useState<ParentRegistrationFormData>({
    tuitionType: '',
    educationSystem: '',
    level: '',
    subject: '',
    teachingLanguages: [],
    lessonDuration: 0,
    lessonsPerWeek: 0,
    tutorRemarks: '',
    state: '',
    district: '',
    areaNote: '',
    preferredTimes: {},
    hourlyRate: 50,
    termsAccepted: true, // Already accepted when the case was originally created
  });

  const progress = ((currentStep + 1) / REGISTRATION_STEPS.length) * 100;

  // Fetch case data on mount
  useEffect(() => {
    const fetchCaseData = async () => {
      if (!caseId) {
        toast.error(isZh ? '找不到个案 ID' : 'Case ID not found');
        navigate('/parent/my-cases');
        return;
      }

      try {
        setIsLoading(true);
        const data = await getCaseById(caseId);

        // Pre-fill form with case data
        setFormData({
          tuitionType: data.case.tuitionType,
          educationSystem: data.case.educationSystem ?? '',
          level: data.case.level,
          subject: data.case.subject,
          teachingLanguages: data.case.teachingLanguages,
          lessonDuration: data.case.lessonDuration,
          lessonsPerWeek: data.case.lessonsPerWeek,
          tutorRemarks: data.case.tutorRemarks ?? '',
          state: data.case.state ?? '',
          district: data.case.district || '',
          areaNote: data.case.areaNote || '',
          preferredTimes: data.case.preferredTimes || {},
          hourlyRate: data.case.hourlyRate || 50,
          termsAccepted: true, // Already accepted when the case was originally created
        });
      } catch (error) {
        logger.error('EditCasePage', 'Failed to fetch case', error);
        toast.error(isZh ? '加载个案详情失败' : 'Failed to load case details');
        navigate('/parent/my-cases');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCaseData();
  }, [caseId, navigate, isZh]);

  const validateCurrentStep = (): boolean => {
    setErrors({});
    const result = parseParentStep(currentStep, formData);
    if (result.success) return true;
    if ('error' in result) {
      const newErrors: Record<string, string> = {};
      (result.error as ZodError).issues.forEach((err) => {
        const field = err.path[0] as string;
        if (!newErrors[field]) newErrors[field] = err.message;
      });
      setErrors(newErrors);
    }
    return false;
  };

  const nextStep = () => {
    const formDataSnapshot = structuredClone(formData);
    logger.log('EditCasePage', `Next click snapshot at step ${currentStep + 1}`, formDataSnapshot, true);

    if (validateCurrentStep() && currentStep < REGISTRATION_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setErrors({});
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = async () => {
    // Check authentication
    if (!backendUser || !caseId) {
      toast.error(isZh ? '请先登录以保存更改' : 'Please log in to save changes');
      return;
    }

    // Validate all steps
    try {
      parentRegistrationSchema.parse(formData);

      setIsSaving(true);
      setErrors({});

      // Call API to update case
      const updatePayload: CreateCaseRequest = {
        tuitionType: formData.tuitionType as CreateCaseRequest['tuitionType'],
        educationSystem: formData.educationSystem,
        level: formData.level as CreateCaseRequest['level'],
        subject: formData.subject,
        teachingLanguages: formData.teachingLanguages,
        lessonsPerWeek: formData.lessonsPerWeek,
        lessonDuration: formData.lessonDuration,
        tutorRemarks: formData.tutorRemarks,
        state: formData.tuitionType === 'online' ? undefined : formData.state,
        district: formData.tuitionType === 'online' ? undefined : (formData.district || undefined),
        areaNote: formData.tuitionType === 'online' ? undefined : (formData.areaNote || undefined),
        preferredTimes: formData.preferredTimes,
        hourlyRate: formData.hourlyRate,
      };

      // If resubmitting a rejected case, set status back to pending_review
      if (isResubmit) {
        updatePayload.status = 'pending_review';
      }

      await updateCase(caseId, updatePayload);

      // Invalidate parent cases query to refresh the list
      await queryClient.invalidateQueries({
        queryKey: ['parentCases'],
      });

      const message = isResubmit
        ? isZh ? '个案已重新提交审核！' : 'Case resubmitted for review!'
        : isZh ? '个案更新成功！' : 'Case updated successfully!';
      toast.success(message);

      // Navigate back after brief delay
      setTimeout(() => {
        navigate('/parent/my-cases');
      }, 1000);
    } catch (error) {
      if (error instanceof ZodError) {
        const newErrors: Record<string, string> = {};
        error.issues.forEach((err) => {
          const field = err.path[0] as string;
          newErrors[field] = err.message;
        });
        setErrors(newErrors);
        toast.error(isZh ? '请正确填写所有必填字段' : 'Please complete all required fields correctly');
      } else {
        logger.error('EditCasePage', 'Failed to update case', error);
        captureError('EditCase:update', error);
        toast.error(isZh ? '更新个案失败' : 'Failed to update case');
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin">
            <div className="w-12 h-12 border-4 border-gray-300 border-t-secondary rounded-full"></div>
          </div>
          <p className="mt-4 text-gray-600">{isZh ? '正在加载个案详情...' : 'Loading case details...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/parent/my-cases')}
            className="flex items-center text-secondary hover:text-secondary-dark mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {isZh ? '返回我的个案' : 'Back to My Cases'}
          </button>
          <h1 className="text-3xl font-bold text-gray-900">{isZh ? '编辑个案' : 'Edit Case'}</h1>
          <p className="text-gray-600 mt-2">{isZh ? '更新你的补习个案资料' : 'Update your tuition case details'}</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">{REGISTRATION_STEPS[currentStep]}</span>
            <span className="text-xs text-gray-500">{currentStep + 1}/{REGISTRATION_STEPS.length}</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-secondary transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="min-h-[400px]">
            {currentStep === 0 && (
              <TuitionDetailsStep
                formData={formData}
                errors={errors}
                onChange={(data) => setFormData({ ...formData, ...data })}
                showBothOption={true}
              />
            )}

            {currentStep === 1 && (
              <SubjectDurationStep
                formData={formData}
                errors={errors}
                onChange={(data) => setFormData({ ...formData, ...data })}
              />
            )}

            {currentStep === 2 && (
              <LocationTimeStep
                formData={formData}
                errors={errors}
                onChange={(data) => setFormData({ ...formData, ...data })}
              />
            )}

            {currentStep === 3 && (
              <BudgetStep
                formData={formData}
                errors={errors}
                onChange={(data) => setFormData({ ...formData, ...data })}
                showTerms={false}
              />
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-8 border-t border-gray-200">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`px-6 py-2 rounded-lg font-semibold ${currentStep === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              {isZh ? '上一步' : 'Previous'}
            </button>
            <div className="flex gap-3">
              {currentStep < REGISTRATION_STEPS.length - 1 ? (
                <button onClick={nextStep} className="btn-secondary">
                  {isZh ? '下一步' : 'Next'}
                </button>
              ) : (
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className={`px-6 py-2 rounded-lg font-semibold flex items-center ${
                    isSaving
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-secondary text-white hover:bg-secondary-dark'
                  }`}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSaving ? (isZh ? '保存中...' : 'Saving...') : (isZh ? '保存更改' : 'Save Changes')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCasePage;
